import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Button from '../components/ui/Button';
import PasswordStrengthMeter from '../components/ui/PasswordStrengthMeter';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { validateGSTIN, validatePAN, validateIndianMobile, validatePinCode, validateEmail, validateGSTINWithPAN } from '../utils/validation';

const businessCategories = [
  { value: '', label: 'Select Business Category' },
  { value: 'Retailer', label: 'Retailer' },
  { value: 'Wholesaler', label: 'Wholesaler' },
  { value: 'Stockist', label: 'Stockist' },
  { value: 'End User', label: 'End User' },
  { value: 'Project', label: 'Project' },
  { value: 'Other', label: 'Other' },
];

const initialForm = {
  // Account
  email: '',
  password: '',
  // Business
  businessName: '',
  businessCategory: '',
  gstNumber: '',
  panNumber: '',
  // Contact
  contactName: '',
  contactRole: '',
  contactEmail: '',
  contactMobile: '',
  contactAltMobile: '',
  // Bill To
  billCountry: '',
  billState: '',
  billDistrict: '',
  billPinCode: '',
  billGpsLocation: '',
  billAddressLine: '',
  // Ship To
  shipCountry: '',
  shipState: '',
  shipDistrict: '',
  shipPinCode: '',
  shipGpsLocation: '',
  shipAddressLine: '',
  // New fields
  salesExecutive: '',
  yearsInBusiness: '',
  bankReference: '',
  competitionBrands: '',
  // Conditional fields
  wholesaleRetailersServed: '',
  wholesaleAreaCoverage: '',
  stockistWholesalesServed: '',
  stockistAreaOperation: '',
  projectName: '',
  // Estimated Volumes
  estimatedVolumes: [],
};

const SectionCard = ({ title, icon, children, className = "" }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6 ${className}`}>
    <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex items-center gap-3">
      <div className="text-blue-600 bg-blue-50 p-2 rounded-lg">
        {icon}
      </div>
      <h2 className="text-lg font-bold text-gray-800">{title}</h2>
    </div>
    <div className="p-6">
      {children}
    </div>
  </div>
);

export default function CustomerRegister() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [salesExecutives, setSalesExecutives] = useState([]);

  useEffect(() => {
    apiService.getPublicProducts().then(res => setProducts(res || [])).catch(() => setProducts([]));
    apiService.getSalesExecutives().then(res => setSalesExecutives(res || [])).catch(() => setSalesExecutives([]));
  }, []);

  const handleCopyBillToShip = () => {
    setForm(prev => ({
      ...prev,
      shipCountry: prev.billCountry,
      shipState: prev.billState,
      shipDistrict: prev.billDistrict,
      shipPinCode: prev.billPinCode,
      shipGpsLocation: prev.billGpsLocation,
      shipAddressLine: prev.billAddressLine,
    }));
    toast.success('Copied Bill To Address to Ship To Address');
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleAddEstimatedVolume = () => {
    setForm(prev => ({
      ...prev,
      estimatedVolumes: [...(prev.estimatedVolumes || []), { productId: '', productName: '', quantity: '' }]
    }));
  };

  const handleRemoveEstimatedVolume = (idx) => {
    setForm(prev => ({
      ...prev,
      estimatedVolumes: prev.estimatedVolumes.filter((_, i) => i !== idx)
    }));
  };

  const handleEstimatedVolumeChange = (idx, field, value) => {
    setForm(prev => ({
      ...prev,
      estimatedVolumes: prev.estimatedVolumes.map((ev, i) =>
        i === idx ? { ...ev, [field]: value, ...(field === 'productId' ? { productName: products.find(p => p.id === parseInt(value))?.name || '' } : {}) } : ev
      )
    }));
  };

  const validate = () => {
    const errs = {};
    let isValid = true;

    // Account & Contact
    if (!form.email) errs.email = 'Email is required';
    else if (!validateEmail(form.email).isValid) errs.email = validateEmail(form.email).error;

    if (!form.password || form.password.length < 6) errs.password = 'Password (min 6 chars) required';

    if (!form.contactName) errs.contactName = 'Contact name is required';

    if (!form.contactMobile) errs.contactMobile = 'Contact mobile is required';
    else if (!validateIndianMobile(form.contactMobile).isValid) errs.contactMobile = validateIndianMobile(form.contactMobile).error;

    if (form.contactAltMobile && !validateIndianMobile(form.contactAltMobile).isValid) {
      errs.contactAltMobile = validateIndianMobile(form.contactAltMobile).error;
    }

    if (form.contactEmail && !validateEmail(form.contactEmail).isValid) {
      errs.contactEmail = validateEmail(form.contactEmail).error;
    }

    // Business
    if (!form.businessName) errs.businessName = 'Business name is required';
    if (!form.businessCategory) errs.businessCategory = 'Business category is required';

    if (form.gstNumber) {
      const gstVal = validateGSTIN(form.gstNumber);
      if (!gstVal.isValid) errs.gstNumber = gstVal.error;
    }

    if (form.panNumber) {
      const panVal = validatePAN(form.panNumber);
      if (!panVal.isValid) errs.panNumber = panVal.error;
    }

    if (form.gstNumber && form.panNumber) {
      const crossVal = validateGSTINWithPAN(form.gstNumber, form.panNumber);
      if (!crossVal.isValid) errs.gstNumber = crossVal.error;
    }

    if (form.yearsInBusiness && (isNaN(form.yearsInBusiness) || form.yearsInBusiness < 0 || form.yearsInBusiness > 200)) {
      errs.yearsInBusiness = 'Invalid years';
    }

    // Addresses
    if (!form.billCountry) errs.billCountry = 'Required';
    if (!form.billState) errs.billState = 'Required';
    if (!form.billDistrict) errs.billDistrict = 'Required';
    if (!form.billPinCode) errs.billPinCode = 'Required';
    else if (!validatePinCode(form.billPinCode).isValid) errs.billPinCode = validatePinCode(form.billPinCode).error;
    if (!form.billAddressLine) errs.billAddressLine = 'Required';

    if (!form.shipCountry) errs.shipCountry = 'Required';
    if (!form.shipState) errs.shipState = 'Required';
    if (!form.shipDistrict) errs.shipDistrict = 'Required';
    if (!form.shipPinCode) errs.shipPinCode = 'Required';
    else if (!validatePinCode(form.shipPinCode).isValid) errs.shipPinCode = validatePinCode(form.shipPinCode).error;
    if (!form.shipAddressLine) errs.shipAddressLine = 'Required';

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      isValid = false;
      toast.error('Please fix the errors before submitting');
      // Scroll to first error
      const firstError = document.querySelector('.text-red-500');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } else {
      setErrors({});
    }

    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await register({
        name: form.contactName,
        email: form.email,
        password: form.password,
        businessName: form.businessName,
        businessCategory: form.businessCategory,
        gstNumber: form.gstNumber,
        panNumber: form.panNumber,
        contactName: form.contactName,
        contactRole: form.contactRole,
        contactEmail: form.contactEmail,
        contactMobile: form.contactMobile,
        contactAltMobile: form.contactAltMobile,
        billCountry: form.billCountry,
        billState: form.billState,
        billDistrict: form.billDistrict,
        billPinCode: form.billPinCode,
        billGpsLocation: form.billGpsLocation,
        billAddressLine: form.billAddressLine,
        shipCountry: form.shipCountry,
        shipState: form.shipState,
        shipDistrict: form.shipDistrict,
        shipPinCode: form.shipPinCode,
        shipGpsLocation: form.shipGpsLocation,
        shipAddressLine: form.shipAddressLine,
        salesExecutiveId: form.salesExecutive,
        yearsInBusiness: form.yearsInBusiness,
        bankReference: form.bankReference,
        competitionBrands: form.competitionBrands,
        estimatedVolumes: form.estimatedVolumes,
        wholesaleRetailersServed: form.wholesaleRetailersServed,
        wholesaleAreaCoverage: form.wholesaleAreaCoverage,
        stockistWholesalesServed: form.stockistWholesalesServed,
        stockistAreaOperation: form.stockistAreaOperation,
        projectName: form.projectName,
      });
      if (res.success) {
        toast.success('Registration submitted! Awaiting admin approval.');
        setForm(initialForm);
        navigate('/');
      } else {
        toast.error(res.error || 'Registration failed');
      }
    } catch (err) {
      toast.error('Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight">Partner Registration</h1>
          <p className="mt-2 text-lg text-gray-600">Join our network and grow your business</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Account & Contact */}
          <SectionCard
            title="Account & Contact Details"
            icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <Input label="Email Address" value={form.email} onChange={e => handleChange('email', e.target.value)} error={errors.email} required placeholder="you@company.com" autoComplete="username" />
              <div>
                <Input label="Password" type="password" value={form.password} onChange={e => handleChange('password', e.target.value)} error={errors.password} required placeholder="••••••••" autoComplete="new-password" />
                <div className="mt-2"><PasswordStrengthMeter password={form.password} /></div>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Contact Person</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Input label="Full Name" value={form.contactName} onChange={e => handleChange('contactName', e.target.value)} error={errors.contactName} required />
                <Input label="Designation / Role" value={form.contactRole} onChange={e => handleChange('contactRole', e.target.value)} placeholder="e.g. Manager" />
                <Input label="Contact Email (Optional)" value={form.contactEmail} onChange={e => handleChange('contactEmail', e.target.value)} error={errors.contactEmail} />
                <Input label="Mobile Number" value={form.contactMobile} onChange={e => handleChange('contactMobile', e.target.value)} error={errors.contactMobile} required placeholder="+91 XXXXX XXXXX" />
                <Input label="Alt. Mobile Number" value={form.contactAltMobile} onChange={e => handleChange('contactAltMobile', e.target.value)} error={errors.contactAltMobile} />
              </div>
            </div>
          </SectionCard>

          {/* Business Details */}
          <SectionCard
            title="Business Information"
            icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <Input label="Registered Business Name" value={form.businessName} onChange={e => handleChange('businessName', e.target.value)} error={errors.businessName} required />
              <Select label="Business Category" value={form.businessCategory} onChange={e => handleChange('businessCategory', e.target.value)} options={businessCategories} error={errors.businessCategory} required />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <Input label="GST Number" value={form.gstNumber} onChange={e => handleChange('gstNumber', e.target.value)} error={errors.gstNumber} placeholder="22AAAAA0000A1Z5" />
              <Input label="PAN Number" value={form.panNumber} onChange={e => handleChange('panNumber', e.target.value)} error={errors.panNumber} placeholder="AAAAA9999A" />
              <Input label="Years in Business" type="number" value={form.yearsInBusiness} onChange={e => handleChange('yearsInBusiness', e.target.value)} error={errors.yearsInBusiness} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                label="Assigned Sales Executive"
                value={form.salesExecutive}
                onChange={e => handleChange('salesExecutive', e.target.value)}
                options={[
                  { value: '', label: 'Select Sales Executive' },
                  ...salesExecutives.map(se => ({ value: se.id.toString(), label: `${se.name} (${se.employeeId || 'N/A'})` }))
                ]}
                error={errors.salesExecutive}

                helperText="Select the executive who referred you or handles your area."
              />
              <Input label="Bank Reference (Optional)" value={form.bankReference} onChange={e => handleChange('bankReference', e.target.value)} />
            </div>

            {/* Conditional Fields */}
            {(form.businessCategory === 'Wholesaler' || form.businessCategory === 'Stockist' || form.businessCategory === 'Project') && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Category Specifics</h3>
                {form.businessCategory === 'Wholesaler' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input label="No. of Retailers Served" type="number" value={form.wholesaleRetailersServed} onChange={e => handleChange('wholesaleRetailersServed', e.target.value)} />
                    <Input label="Area of Coverage" value={form.wholesaleAreaCoverage} onChange={e => handleChange('wholesaleAreaCoverage', e.target.value)} />
                  </div>
                )}
                {form.businessCategory === 'Stockist' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input label="No. of Wholesale Shops Served" type="number" value={form.stockistWholesalesServed} onChange={e => handleChange('stockistWholesalesServed', e.target.value)} />
                    <Input label="Area of Operation" value={form.stockistAreaOperation} onChange={e => handleChange('stockistAreaOperation', e.target.value)} />
                  </div>
                )}
                {form.businessCategory === 'Project' && (
                  <div>
                    <Input label="Project Name" value={form.projectName} onChange={e => handleChange('projectName', e.target.value)} />
                  </div>
                )}
              </div>
            )}
          </SectionCard>

          {/* Addresses */}
          <SectionCard
            title="Address Details"
            icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Billing Address */}
              <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                <h3 className="text-md font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500"></span> Billing Address
                </h3>
                <div className="space-y-4">
                  <Input label="Address Line" value={form.billAddressLine} onChange={e => handleChange('billAddressLine', e.target.value)} error={errors.billAddressLine} required />
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="City / District" value={form.billDistrict} onChange={e => handleChange('billDistrict', e.target.value)} error={errors.billDistrict} required />
                    <Input label="State" value={form.billState} onChange={e => handleChange('billState', e.target.value)} error={errors.billState} required />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="Pin Code" value={form.billPinCode} onChange={e => handleChange('billPinCode', e.target.value)} error={errors.billPinCode} required />
                    <Input label="Country" value={form.billCountry} onChange={e => handleChange('billCountry', e.target.value)} error={errors.billCountry} required />
                  </div>
                  <Input label="GPS Location (Optional)" value={form.billGpsLocation} onChange={e => handleChange('billGpsLocation', e.target.value)} placeholder="Latitude, Longitude" />
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-md font-bold text-gray-800 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span> Shipping Address
                  </h3>
                  <Button type="button" variant="outline" size="sm" onClick={handleCopyBillToShip} className="text-xs py-1 h-auto">
                    Copy Billing
                  </Button>
                </div>
                <div className="space-y-4">
                  <Input label="Address Line" value={form.shipAddressLine} onChange={e => handleChange('shipAddressLine', e.target.value)} error={errors.shipAddressLine} required />
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="City / District" value={form.shipDistrict} onChange={e => handleChange('shipDistrict', e.target.value)} error={errors.shipDistrict} required />
                    <Input label="State" value={form.shipState} onChange={e => handleChange('shipState', e.target.value)} error={errors.shipState} required />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="Pin Code" value={form.shipPinCode} onChange={e => handleChange('shipPinCode', e.target.value)} error={errors.shipPinCode} required />
                    <Input label="Country" value={form.shipCountry} onChange={e => handleChange('shipCountry', e.target.value)} error={errors.shipCountry} required />
                  </div>
                  <Input label="GPS Location (Optional)" value={form.shipGpsLocation} onChange={e => handleChange('shipGpsLocation', e.target.value)} placeholder="Latitude, Longitude" />
                </div>
              </div>
            </div>
          </SectionCard>

          {/* Business Insights */}
          <SectionCard
            title="Business Insights"
            icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>}
          >
            <div className="mb-6">
              <Input label="Which competitor brands are you currently dealing with?" value={form.competitionBrands} onChange={e => handleChange('competitionBrands', e.target.value)} placeholder="e.g. Brand A, Brand B" />
            </div>

            <div className="border-t border-gray-100 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Estimated Monthly Volumes</h3>
                <Button type="button" variant="outline" size="sm" onClick={handleAddEstimatedVolume}>
                  + Add Product
                </Button>
              </div>

              {form.estimatedVolumes.length === 0 && (
                <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <p className="text-gray-500 text-sm">No products added yet.</p>
                  <p className="text-gray-400 text-xs mt-1">Add products to help us serve you better.</p>
                </div>
              )}

              <div className="space-y-3">
                {form.estimatedVolumes.map((ev, idx) => (
                  <div key={idx} className="flex flex-col sm:flex-row gap-3 items-start sm:items-end bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <div className="flex-grow w-full sm:w-auto">
                      <Select
                        label="Product"
                        value={ev.productId}
                        onChange={e => handleEstimatedVolumeChange(idx, 'productId', e.target.value)}
                        options={[
                          { value: '', label: 'Select Product' },
                          ...products.map(p => ({ value: p.id.toString(), label: p.name }))
                        ]}
                        required
                      />
                    </div>
                    <div className="w-full sm:w-32">
                      <Input
                        label="Quantity"
                        type="number"
                        value={ev.quantity}
                        onChange={e => handleEstimatedVolumeChange(idx, 'quantity', e.target.value)}
                        required
                        placeholder="Qty"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      onClick={() => handleRemoveEstimatedVolume(idx)}
                      className="mb-1 w-full sm:w-auto"
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </SectionCard>

          <div className="sticky bottom-4 z-10">
            <div className="bg-white/80 backdrop-blur-md p-4 rounded-xl shadow-lg border border-blue-100 flex items-center justify-between">
              <div className="text-sm text-gray-600 hidden sm:block">
                By registering, you agree to our <a href="#" className="text-blue-600 hover:underline">Terms of Service</a>.
              </div>
              <Button type="submit" loading={loading} className="w-full sm:w-auto px-8 py-3 text-lg font-bold bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg transform transition hover:-translate-y-0.5">
                Complete Registration
              </Button>
            </div>
          </div>

        </form>

        <div className="mt-8 text-center text-sm text-gray-500 pb-8">
          Already have an account? <a href="/customer/login" className="text-blue-600 hover:underline font-medium">Login here</a>
        </div>
      </div>
    </div>
  );
}